import { useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import api from "@/lib/api";
import VestaFlame from "./VestaFlame";
import { ClienteView } from "@/components/cliente/ClienteView";
import { staffSource } from "@/components/cliente/clienteApi";

const BASE = "/public/vesta-demo/m";
const PLANT = "FIER-FabioFerlucci";
const T = {
  it: { hey: "Ehi Vesta, presentati", heyQ: "Ehi Vesta", tap: "Tocca il fuoco per parlare", stop: "Ti ascolto…", end: "Vesta sta parlando…", think: "Sto pensando…", nohear: "Non ho sentito bene: tocca la fiamma e riprova.", plantQ: "Ho appena aperto il pannello dell'impianto demo: raccontami in breve cosa sto vedendo, com'è la situazione adesso, e poi chiedimi cosa voglio approfondire.", open: "Mostra l'impianto demo dal vivo", close: "Nascondi l'impianto demo", mon: "Chiedi a Vesta tutto quello che vuoi sapere sull'impianto demo",
    capDay: "Per oggi la demo è al completo: riprova domani!", capUser: "Hai raggiunto il limite di domande della demo. Grazie per aver provato Ai VESTA!", err: "Vesta non è raggiungibile, riprova.", mic: "Microfono non disponibile: consenti l'accesso al microfono e riprova." },
  en: { hey: "Hey Vesta, introduce yourself", heyQ: "Hey Vesta, introduce yourself", tap: "Tap the fire to speak", stop: "I'm listening…", end: "Vesta is speaking…", think: "Thinking…", nohear: "I didn't hear you well: tap the flame and retry.", plantQ: "I just opened the demo plant panel: briefly tell me what I am looking at and how things are right now, then ask me what I would like to explore.", open: "Show the live demo plant", close: "Hide the demo plant", mon: "Ask Vesta anything you want to know about the demo plant",
    capDay: "Today's demo is fully booked: try again tomorrow!", capUser: "You reached the demo question limit. Thanks for trying Ai VESTA!", err: "Vesta is not reachable, please retry.", mic: "Microphone not available: allow microphone access and retry." },
};

const SILENCE_MS = 1300, MAX_MS = 20000;

function useVoice(lang, onTurn) {
  const r = useRef({ sid: "", audio: new Audio(), greeted: false, alive: false, stream: null, ctx: null, mr: null });
  const [state, setState] = useState("idle");
  const play = (b64) => new Promise((res) => {
    const a = r.current.audio; a.src = `data:audio/mpeg;base64,${b64}`; setState("speaking");
    a.onended = res; a.onerror = res; a.play().catch(res);
  });
  const stop = () => {
    const S = r.current; S.alive = false; S.audio.pause();
    if (S.mr?.state === "recording") S.mr.stop();
    S.stream?.getTracks().forEach((t) => t.stop()); S.stream = null; setState("idle");
  };
  const fail = (msg) => { onTurn({ error: msg }); stop(); };
  const listen = async () => {
    const S = r.current;
    if (!S.alive) return;
    try { S.stream = S.stream || await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } }); }
    catch { fail(T[lang].mic); return; }
    const an = S.ctx.createAnalyser(); an.fftSize = 512;
    S.ctx.createMediaStreamSource(S.stream).connect(an);
    const buf = new Uint8Array(an.fftSize);
    const mime = ["audio/webm", "audio/mp4"].find((m) => window.MediaRecorder?.isTypeSupported?.(m));
    const mr = new MediaRecorder(S.stream, mime ? { mimeType: mime } : undefined); S.mr = mr;
    const chunks = []; let spoke = false, quiet = 0; const t0 = Date.now();
    mr.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    mr.onstop = () => {
      if (!S.alive) return;
      if (!spoke) { stop(); return; }
      const fd = new FormData(); fd.append("audio", new Blob(chunks, { type: mr.mimeType }), mr.mimeType.includes("mp4") ? "v.mp4" : "v.webm"); send(fd);
    };
    setState("listening"); mr.start(200);
    const tick = () => {
      if (mr.state !== "recording") return;
      an.getByteTimeDomainData(buf);
      const rms = Math.sqrt(buf.reduce((x, v) => x + ((v - 128) / 128) ** 2, 0) / buf.length), now = Date.now();
      if (rms > 0.06) { spoke = true; quiet = 0; } else if (spoke && !quiet) quiet = now;
      if (!S.alive || (spoke && quiet && now - quiet > SILENCE_MS) || now - t0 > MAX_MS || (!spoke && now - t0 > 10000)) { mr.stop(); return; }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };
  const after = async (b64) => { await play(b64); if (r.current.alive) listen(); else setState("idle"); };
  const send = async (fd) => {
    const S = r.current; setState("thinking");
    fd.append("session_id", S.sid); fd.append("lang", lang);
    try {
      const { data } = await api.post("/public/vesta-demo/turn", fd, { timeout: 120000 });
      S.sid = data.session_id; onTurn(S.auto ? { ...data, domanda: null } : data); S.auto = false; await after(data.audio);
    } catch (e) { const d = e.response?.data?.detail; fail(d === "cap_day" ? T[lang].capDay : d === "cap_user" ? T[lang].capUser : d === "vuoto" || d === "audio" ? T[lang].nohear : T[lang].err); }
  };
  const start = () => {
    const S = r.current; S.alive = true;
    S.ctx = S.ctx || new (window.AudioContext || window.webkitAudioContext)(); S.ctx.resume?.();
    S.audio.play().catch(() => {});
  };
  const greet = async () => {
    r.current.greeted = true; setState("thinking");
    try { const { data } = await api.get("/public/vesta-demo/saluto", { params: { lang }, timeout: 60000 }); onTurn({ ...data, saluto: true }); await after(data.audio); }
    catch { fail(T[lang].err); }
  };
  const ask = (testo, auto) => { const S = r.current; S.greeted = true; S.auto = auto; S.audio.pause(); start(); const fd = new FormData(); fd.append("testo", testo); send(fd); };
  const toggle = () => {
    const S = r.current;
    if (S.alive) { stop(); return; }
    start();
    if (!S.greeted) greet(); else listen();
  };
  return { state, ask, toggle };
}

function Monitoring({ t, open, setOpen }) {
  const src = useMemo(() => staffSource(BASE, PLANT, {}), []);
  return (
    <section className="flex w-full flex-col items-center gap-3" data-testid="vesta-demo-monitoring">
      <button type="button" onClick={() => setOpen(!open)} data-testid="vesta-demo-toggle-plant"
        className="inline-flex items-center gap-2 rounded-full border border-neutral-700 px-4 py-2 text-xs font-bold uppercase tracking-widest text-neutral-200 transition-colors hover:border-[#C17F59]">
        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}{open ? t.close : t.open}</button>
      {open && <div className="w-full max-w-md space-y-3" data-testid="vesta-demo-plant-panel">
        <div className="text-center text-[11px] font-bold uppercase tracking-widest text-[#C17F59]" data-testid="vesta-demo-mon-title">{t.mon}</div>
        <div className="pointer-events-none select-none" aria-hidden="true">
          <ClienteView src={src} embed />
        </div>
      </div>}
    </section>
  );
}

export default function VestaDemo() {
  const [lang, setLang] = useState(new URLSearchParams(window.location.search).get("lang") === "en" ? "en" : "it");
  const [log, setLog] = useState([]);
  const [open, setOpen] = useState(false);
  const [heyDone, setHeyDone] = useState(false);
  const t = T[lang];
  const v = useVoice(lang, (d) => { setLog((x) => [...x.slice(-5), d]); if (d.show) setOpen(true); });
  const last = log[log.length - 1];
  const busy = v.state === "thinking";
  return (
    <div className="min-h-screen bg-black text-white" data-testid="vesta-demo">
      <div className="relative mx-auto flex min-h-screen max-w-5xl flex-col items-center gap-8 px-4 pb-14 pt-12 sm:gap-10">
        <div className="absolute right-4 top-4 flex gap-1">{["it", "en"].map((l) => (
          <button key={l} type="button" onClick={() => setLang(l)} data-testid={`vesta-demo-lang-${l}`}
            className={`rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-widest transition-colors ${lang === l ? "border-[#C17F59] text-[#E8B48F]" : "border-neutral-800 text-neutral-500 hover:text-white"}`}>{l}</button>))}</div>
        <button type="button" onClick={v.toggle} className="rounded-full p-2 transition-transform hover:scale-105" data-testid="vesta-demo-flame" aria-label={t.tap}>
          <VestaFlame size={182} state={v.state} />
        </button>
        <p className="text-sm font-semibold tracking-wide text-[#E8B48F]" data-testid="vesta-demo-state">{v.state === "listening" ? t.stop : v.state === "speaking" ? t.end : v.state === "thinking" ? t.think : t.tap}</p>
        {!heyDone && <button type="button" disabled={busy} onClick={() => { setHeyDone(true); v.ask(t.heyQ); }} data-testid="vesta-demo-hey" className="rounded-full border border-[#C17F59] px-4 py-2 text-xs font-bold uppercase tracking-widest text-[#E8B48F] transition-colors hover:bg-[#C17F59]/15 disabled:opacity-40">{t.hey}</button>}
        {last && <div className="w-full max-w-2xl rounded-2xl border border-neutral-800 bg-neutral-950 p-4 text-sm" data-testid="vesta-demo-reply">
          {last.error ? <p className="text-amber-300" data-testid="vesta-demo-error">{last.error}</p> : <>
            {last.domanda && <p className="mb-2 text-xs text-neutral-500">“{last.domanda}”</p>}<p className="leading-relaxed text-neutral-200">{last.risposta}</p></>}
        </div>}
        <Monitoring t={t} open={open} setOpen={(x) => { setOpen(x); if (x && v.state === "idle") v.ask(t.plantQ, true); }} />
      </div>
    </div>
  );
}
